import { ref, watch } from "vue";
import { Loader } from "@sazka/design-system/src/loader/loader";
import { isMobileApp, triggerMobileAppEvent } from "@sazka/mobile-apps/mobile-app-integration";
import { useNg3DsRedirection } from "@sazka/ng/ng-3ds-redirection";
import { ngFrameworkLoaded, ngFrameworkLoadedSuccessfully } from "@sazka/ng/ng-integration";
import { useNgSession } from "@sazka/ng/ng-session";
import { getAppDeepLink } from "@sazka/utils/app-deep-link";
import { UrlUtils } from "@sazka/utils/url";
import { useCasinoCheckoutManager } from "./casino-checkout-manager";
import { handleFullscreenCapture } from "./ng-casino-fullscreen";
import { CasinoGameLoadParams } from "../model/casino-game-load-params";

let casinoModule: typeof import("./ng-casino-module") = null;
let isCasinoHistoryDisabled = false;
export const ngCasinoModuleLoaded = ref(false);

export const useNgCasinoLib = async () => {
    registerGameClick();

    await ngFrameworkLoaded();

    startCasinoGameFromQuery();
    registerNgHsCallbacks();
};

function startCasinoGameFromQuery() {
    const id = UrlUtils.getQueryString("game");
    if (id) {
        loadCasinoModule().then((x) =>
            x.useNgCasino().loadGame(
                {
                    gameId: parseInt(id),
                },
                true
            )
        );
        UrlUtils.clearUrlParam("game");
    }
}

function handleCasinoHistory() {
    if (!casinoModule || isCasinoHistoryDisabled) return;

    const { currentGameId, closeGame } = casinoModule.useNgCasino();
    window.addEventListener("popstate", () => {
        if (currentGameId.value) {
            closeGame();
        }
    });
    watch(currentGameId, (val) => {
        if (val) {
            // opening game
            history.pushState({ game: currentGameId.value }, "", UrlUtils.getUrlWithoutParams());
        } else {
            // closing game
            if (history.state?.game && !window.isNavigating) {
                history.back(); // remove the pushed state
            }
            UrlUtils.clearUrlParam("game");
        }
    });
}

export function disableCasinoHistory() {
    isCasinoHistoryDisabled = true;
}

export async function loadCasinoModule() {
    if (!casinoModule) {
        // loader is closed by game load start
        try {
            Loader.show();
            casinoModule = await import(/* webpackPrefetch: true */ "./ng-casino-module");
            ngCasinoModuleLoaded.value = true;

            handleCasinoHistory();
            handleFullscreenCapture(casinoModule);
        } catch (e) {
            console.error(e);
        } finally {
            Loader.hide();
        }
    }

    return casinoModule;
}

function parseMaybeBooleanAttribute(value: string | boolean | null): boolean | undefined {
    if (value?.toString().toLowerCase() === "true") return true;
    if (value?.toString().toLowerCase() === "false") return false;
    return undefined;
}

function handleMobileAppGameLaunch(gameId: number, moneyMode: boolean | undefined, isLoggedIn: boolean): void {
    if (moneyMode === true && !isLoggedIn) {
        triggerMobileAppEvent("goToDeepLink", getAppDeepLink("login"));
        return;
    }

    if (typeof moneyMode !== "boolean") {
        moneyMode = isLoggedIn;
    }
    const mode = moneyMode === true ? "money" : "fun";
    triggerMobileAppEvent("goToDeepLink", `sazka://casino/games/${gameId}?mode=${mode}`);
}

function registerGameClick() {
    document.addEventListener("click", async (e) => {
        const clickedElement = e.target as Element;
        const gameLauncher = clickedElement.closest(".game-launcher");
        if (!gameLauncher) return;

        e.preventDefault();

        const gameId = Number(gameLauncher.getAttribute("data-game-id"));
        const manufacturer = gameLauncher.getAttribute("data-manufacturer-name");
        const showLogin = Boolean(gameLauncher.getAttribute("data-show-login"));
        const moneyMode = parseMaybeBooleanAttribute(gameLauncher.getAttribute("data-money-mode"));
        const isTransparent = parseMaybeBooleanAttribute(gameLauncher.getAttribute("data-transparent"));
        const withAbTest = gameLauncher.getAttribute("data-ab-test") !== "false";

        const { isLoggedIn } = useNgSession();

        if (isMobileApp) {
            handleMobileAppGameLaunch(gameId, moneyMode, isLoggedIn.value);
            return;
        }

        if (!ngFrameworkLoadedSuccessfully.value) {
            Loader.show();
            await ngFrameworkLoaded();
            Loader.hide();
        }

        let position = 0;
        const wrapper = gameLauncher.closest("[data-a-games-placement]");
        let placement = wrapper?.getAttribute("data-a-games-placement") || null;

        if (placement) {
            const gamesInPlacement = wrapper.querySelectorAll(".game-launcher");
            const filteredGames = Array.from(gamesInPlacement).filter((g) => {
                const el = g as HTMLElement;
                return el.style.display !== "none" && el.parentElement?.style.display !== "none";
            });
            position = filteredGames.indexOf(gameLauncher as HTMLElement) + 1;

            const isNumberedPlacement = wrapper.getAttribute("data-a-numbered-placement");
            if (isNumberedPlacement) {
                const placementWrappers = Array.from(
                    document.querySelectorAll<HTMLElement>("[data-a-numbered-placement]")
                );
                const numberedPlacements = placementWrappers.map((d) => d.dataset.aGamesPlacement);
                const placementIndex = numberedPlacements.indexOf(placement);
                if (placementIndex > -1) {
                    placement = `${placementIndex + 1}.${placement}`;
                }
            }
        }

        const callback = async () => {
            const casinoModule = await loadCasinoModule();
            const { loadGame, handleTransparentProductCardClick } = casinoModule.useNgCasino();
            const gameLoadParams: CasinoGameLoadParams = {
                gameId,
                moneyMode,
            };
            if (isTransparent) {
                handleTransparentProductCardClick(gameId, gameLoadParams);
            } else {
                await loadGame(gameLoadParams, false, manufacturer, placement, position, withAbTest);
            }
        };

        if (showLogin && !isLoggedIn.value) {
            ng_fw.showLogin();
            watch(isLoggedIn, (val) => {
                if (val) {
                    callback();
                }
            });
        } else {
            callback();
        }
    });
}

async function registerNgHsCallbacks() {
    ng_hs.instantGame.gameLoadStart((gameId: number, playMode: "M" | "D") => {
        loadCasinoModule().then((x) => x.useNgCasino().ngGameReady(gameId, playMode));
    });

    useNg3DsRedirection().beforeRedirection(async () => {
        await (await useCasinoCheckoutManager()).onDepositRedirectionStart();
    });

    (await useCasinoCheckoutManager()).registerCheckoutCallbacks();
}
